import React, { useEffect, useState } from 'react'
import { LayoutChangeEvent, View, ViewStyle } from 'react-native'
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

interface Props {
  children: React.ReactNode
  isLoading?: boolean
  style?: ViewStyle
}

const Skeleton = ({ children, isLoading, style }: Props) => {
  const { theme } = useUnistyles()
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  const opacity = useSharedValue(1)

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout

    if (dimensions.width === width && dimensions.height === height) {
      return
    }

    setDimensions({ width, height })
  }

  useEffect(() => {
    if (isLoading) {
      opacity.value = withRepeat(
        withTiming(0.5, {
          duration: 750,
          easing: Easing.inOut(Easing.ease),
        }),
        -1,
        true
      )
    } else {
      opacity.value = 1
    }
  }, [isLoading, opacity])

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
    }
  })

  const overlayBase: ViewStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: dimensions.width || '100%',
    height: dimensions.height || '100%',
    overflow: 'hidden',
  }

  return (
    <View onLayout={handleLayout} style={styles.container}>
      {children}

      {isLoading && (
        <>
          <View
            style={{
              ...overlayBase,
              backgroundColor: theme.colors.surface,
              zIndex: 1000,
            }}
          />
          <Animated.View
            style={[
              {
                ...overlayBase,
                backgroundColor: theme.colors.backdrop,
                borderRadius: theme.tokens.containerBorderRadius,
                zIndex: 1001,
                ...style,
              },
              animatedStyle,
            ]}
          />
        </>
      )}
    </View>
  )
}

const styles = StyleSheet.create(() => {
  return {
    container: {
      minHeight: 32,
    },
  }
})

export default Skeleton
